import {
  addBreadcrumb,
  captureApiError,
  extractRequestId,
  withRequestIdHeaders,
} from '@sazeo/observability';
import { getTenantClient } from '@sazeo/supabase-tenant';

/**
 * Build the absolute Edge Function URL.
 *
 * Tenant subdomains (`{slug}.app.sazeo.io`) are served by the Cloudflare
 * Worker SPA — there is no `/functions/v1` proxy on those hosts. A relative
 * path therefore returns index.html with a 200, which the JSON parsers in
 * this module + SDK hooks misread as an empty response and surface as
 * `error_title`. Always hit Supabase directly via VITE_SUPABASE_URL.
 *
 * Falls back to the relative path only as a defensive last resort (the env
 * is guaranteed by the inline `__SAZEO_VITE_ENV__` boot script in every
 * deployed index.html).
 */
function toFunctionUrl(path: string): string {
  const cleaned = path.replace(/^\/+/, '');
  const supaUrl =
    (import.meta.env?.VITE_SUPABASE_URL as string | undefined) ??
    (typeof globalThis !== 'undefined'
      ? (globalThis as { __SAZEO_VITE_ENV__?: { VITE_SUPABASE_URL?: string } }).__SAZEO_VITE_ENV__
          ?.VITE_SUPABASE_URL
      : undefined);
  if (supaUrl) {
    return `${supaUrl.replace(/\/+$/, '')}/functions/v1/${cleaned}`;
  }
  return `/functions/v1/${cleaned}`;
}

async function getTenantAuthHeaders(): Promise<Record<string, string>> {
  try {
    const client = getTenantClient();
    const { data } = await client.auth.getSession();
    const token = data.session?.access_token;
    return token ? { authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function buildHeaders(
  initHeaders: HeadersInit | undefined,
  authHeaders: Record<string, string>,
  body: BodyInit | null | undefined,
): Headers {
  const headers = new Headers(initHeaders);
  if (
    body != null &&
    !(typeof FormData !== 'undefined' && body instanceof FormData) &&
    !headers.has('content-type')
  ) {
    headers.set('content-type', 'application/json');
  }
  for (const [key, value] of Object.entries(authHeaders)) {
    if (!headers.has(key)) headers.set(key, value);
  }
  return headers;
}

export async function tenantFunctionFetch(path: string, init?: RequestInit): Promise<Response> {
  const authHeaders = await getTenantAuthHeaders();
  const enriched = withRequestIdHeaders(authHeaders);
  const res = await fetch(toFunctionUrl(path), {
    ...init,
    headers: buildHeaders(init?.headers, enriched, init?.body),
  });
  extractRequestId(res.headers);
  return res;
}

export async function tenantFunctionJson<T>(path: string, init?: RequestInit): Promise<T> {
  const url = toFunctionUrl(path);
  const method = (init?.method ?? 'POST').toUpperCase();
  const startedAt = Date.now();

  addBreadcrumb({ action: 'api_request_started', apiUrl: url, apiMethod: method });

  let res: Response;
  try {
    res = await tenantFunctionFetch(path, init);
  } catch (err) {
    const durationMs = Date.now() - startedAt;
    captureApiError({ url, method, status: 0, durationMs });
    addBreadcrumb({
      action: 'api_request_failed',
      apiUrl: url,
      apiMethod: method,
      error: String(err),
    });
    throw err;
  }

  const requestId = res.headers.get('x-request-id') ?? undefined;
  const durationMs = Date.now() - startedAt;

  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    captureApiError({ url, method, status: res.status, requestId, durationMs, body });
    addBreadcrumb({
      action: 'api_request_failed',
      apiUrl: url,
      apiMethod: method,
      statusCode: res.status,
      requestId,
    });
    throw new Error(body.error?.message ?? res.statusText);
  }

  addBreadcrumb({
    action: 'api_request_succeeded',
    apiUrl: url,
    apiMethod: method,
    statusCode: res.status,
    requestId,
  });

  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}
